import React, { createContext, useContext, useState, useEffect } from 'react';
import { GetUserUseQuery } from '../api/api';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from '../components/loading';

// Color palette that centralizes all hardcoded colors found throughout the app
const colorPalette = {
  // Primary brand colors
  primary: {
    main: '#2D55C9',        // Main primary blue used throughout app
    light: '#C7D1FF',       // Light blue for buttons and selections
    selected: '1D1B2029',    // Selected state
    dark: '#1E3A8A',        // Darker primary for hover states
    contrastText: '#FFFFFF', // Text on primary backgrounds
  },

  // Secondary colors
  secondary: {
    main: '#747685',        // Medium gray for borders and secondary elements
    light: '#E0E0E0',       // Light gray for borders and dividers
    dark: '#444653',        // Dark gray for text and labels
    contrastText: '#FFFFFF',
  },

  // Success, warning, error states
  success: {
    main: '#219150',        // Green for success states
    light: '#4CAF50',       // Lighter green
    dark: '#1B5E20',        // Darker green
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#ff9d00',        // Orange for warnings
    light: '#E8C9A3',       // Muted warm gold for borders
    dark: '#F57C00',        // Darker orange
    contrastText: '#000000',
  },

  error: {
    main: '#d32f2f',        // Standard error red
    light: '#E3B3B3',       // Muted soft red for borders
    dark: '#C62828',        // Darker red
    contrastText: '#FFFFFF',
  },

  // Background colors
  background: {
    default: '#FFFFFF',      // Default white background
    card: '#FFFFFF',        // Paper/card backgrounds
    toast: '#363640',        // Toast background
    paper: '#F3f2fd',        // Paper/card backgrounds
    primary: '#FAF8FF',      // Primary light purple background
    secondary: '#e8e7f1',    // Secondary light gray background
    accent: '#eeedf7',       // Accent light purple background
    highlight: '#e0e0f5',    // Highlight light purple background
    popover: '#f3f2fd',      // Popover background
    muted: '#F3f2fd',        // Paper component background
    overlay: 'rgba(255, 255, 255, 0.75)', // Map and overlay backgrounds
    disabled: 'rgba(26, 27, 34, 0.03)',   // Disabled element backgrounds
    alert: 'rgba(29, 113, 219, 0.1)'
  },

  // Text colors
  text: {
    primary: '#000000',      // Primary black text
    secondary: '#191c1d',    // Secondary dark gray text
    muted: '#55575e',        // Muted medium gray text
    disabled: '#767b85', // Disabled text color
    disabledCheckbox: '#acadb6', // Disabled text color
    label: '#444653',        // Label text color
    caption: '#747685',      // Caption and icon text
    avatar: '#44474E',       // Avatar text color
    data: '#262527',         // Data legend text
    hint: '#888888',         // Hint text color
    toast: '#fafafc',        // Toast text color
    highlight: '#181C36',    // --Schemes-Surface-Container
  },

  // Border colors
  border: {
    default: '#b3b1b5',      // Default black borders
    light: '#807e82',        // Light gray borders
    muted: '#E0E0E0',        // Very light gray borders
    card: '#C4C5D6',         // Card borders
    focus: '#2D55C9',        // Focused element borders
    warning: '#E8C9A3',      // Warning state borders
    error: '#E3B3B3',        // Error state borders
    divider: '#e0e0e0',      // Accordion and divider borders
  },

  // Interactive states
  hover: {
    primary: 'rgba(45, 85, 201, 0.08)',     // Primary hover background
    secondary: 'rgb(222, 222, 250)',  // Secondary hover background
    muted: 'rgba(29, 27, 32, 0.08)',        // Muted hover background
    button: 'rgba(0, 0, 0, 0.08)',          // Button hover background
    surface: '#f5f5f5',                     // Surface hover background
  },

  // Legacy colors (from existing CSS)
  legacy: {
    blue: {
      primary: '#007bff',     // Legacy primary blue
      dark: '#004080',        // Legacy dark blue
      focus: '#0056b3',       // Legacy focus blue
      light: '#00c3ff',       // Legacy light blue
      material: '#2196f3',    // Material design blue
      accent: '#3366ff',      // Accent blue
      brand: '#0084ff',       // Brand blue
      info: '#2962FF',        // Info blue
    },
    orange: {
      primary: '#ff9d00',     // Primary orange
      light: '#ffa500',       // Light orange
    },
    misc: {
      teal: '#5cadc1d5',      // Teal accent
      brown: '#423636',       // Brown text
      yellow: '#cfc920',      // Yellow accent
      gray: '#888888',        // Misc gray
      lightGray: '#ccc',      // Light gray
      surface: '#cdcbc5',     // Surface gray
      purple: '#7d5fc9',     // Purple accent
    },
  },

  // Shadows and effects
  shadow: {
    default: '0px 0px 10px 0px rgba(0,0,0,0.1)',
    avatar: '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
    card: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },

  // Opacity levels
  opacity: {
    disabled: 0.38,
    hover: 0.08,
    focus: 0.12,
    selected: 0.16,
    backdrop: 0.75,
  },

  // Border colors
  heatmapBorder: {
    default: '#8E909F',   // --Schemes-Outline
    light: '#444653',     // --Schemes-Surface-Variant
    muted: '#C4C5D6',     // --Schemes-Outline-Variant
    card: '#1E1F27',      // --Schemes-Surface-Container
    focus: '#B6C4FF',     // --Schemes-Primary
    divider: '#444653',   // --Schemes-Surface-Variant
    critical: '#BA1A1A',
    danger: '#FB532D',
    caution: '#FFB94D',
    neutral: '#77767A',
  }
};

// Theme modes
const themes = {
  light: {
    palette: colorPalette,
    mode: 'light',
  },
  dark: {
    palette: {
      // Primary brand colors - mapped from UX designer dark palette
      primary: {
        main: '#708cfa',        // --Schemes-Primary: rgba(182, 196, 255, 1.00)
        light: '#4870E1',       // --Schemes-Primary-Container: rgba(72, 108, 225, 1.00)
        selected: '#FFFFFF29',    // Selected state
        dark: '#2D55C9',        // --Schemes-Primary-40: rgba(45, 85, 201, 1.00)
        contrastText: '#00277E', // --Schemes-On-Primary: rgba(0, 39, 126, 1.00)
      },

      // Secondary colors - mapped from UX designer dark palette
      secondary: {
        main: '#B8C4F8',        // --Schemes-Secondary: rgba(184, 196, 248, 1.00)
        light: '#C9D3FF',       // --Schemes-On-Secondary-Container: rgba(201, 211, 255, 1.00)
        dark: '#314D68',        // --Schemes-Secondary-Container: rgba(49, 61, 104, 1.00)
        contrastText: '#222A58', // --Schemes-On-Secondary: rgba(34, 46, 88, 1.00)
      },

      // Success, warning, error states - mapped from UX designer dark palette
      success: {
        main: '#43a047',        // --Schemes-Tertiary: rgba(164, 202, 244, 1.00)
        light: '#66BB6A',       // --Schemes-On-Tertiary-Container: rgba(255, 255, 255, 1.00)
        dark: '#2E7D32',        // --Schemes-Tertiary-Container: rgba(82, 120, 157, 1.00)
        contrastText: '#014522', // --Schemes-On-Tertiary: rgba(0, 51, 84, 1.00)
      },

      warning: {
        main: '#FFB300',        // --Extended-Colors-Custom-Color-1: rgba(62, 99, 121, 1.00)
        light: '#c0e3fc',       // --Extended-Colors-Custom-Color-1-Container: rgba(202, 233, 255, 1.00)
        dark: '#FB8C00',        // --Extended-Colors-On-Custom-Color-1-Container: rgba(39, 77, 98, 1.00)
        contrastText: '#FFFFFF', // --Extended-Colors-On-Custom-Color-1: rgba(255, 255, 255, 1.00)
      },

      error: {
        main: '#f44336',        // --Schemes-Error: rgba(255, 180, 171, 1.00)
        light: '#FFDAD6',       // --Schemes-On-Error-Container: rgba(255, 218, 214, 1.00)
        dark: '#D32F2F',        // --Schemes-Error-Container: rgba(147, 0, 10, 1.00)
        contrastText: '#690005', // --Schemes-On-Error: rgba(105, 0, 5, 1.00)
      },

      // Background colors - mapped from UX designer dark palette
      background: {
        default: '#12131A',      // --Schemes-Background: rgba(18, 19, 26, 1.00)
        card: '#1E1F27',        // --Schemes-Surface-Container: rgba(30, 31, 39, 1.00)
        toast: '#E2E1EC',        // --Schemes-Inverse-On-Surface: rgba(226, 225, 236, 1.00)
        paper: '#1A1B22',        // --Schemes-Surface-Container-Low: rgba(26, 27, 34, 1.00)
        primary: '#0C0E15',      // --Schemes-Surface-Container-Lowest: rgba(12, 14, 21, 1.00)
        secondary: '#282A31',    // --Schemes-Surface-Container-High: rgba(40, 42, 49, 1.00)
        accent: '#33343C',       // --Schemes-Surface-Container-Highest: rgba(51, 52, 60, 1.00)
        highlight: '#202336',    // --Schemes-Surface-Container
        popover: '#1E1F27',      // --Schemes-Surface-Container
        muted: '#12131A',        // --Schemes-Background
        overlay: 'rgba(0, 0, 0, 0.75)', // Dark overlay backgrounds
        disabled: 'rgba(255, 255, 255, 0.07)', // Disabled backgrounds for dark mode
        alert: 'rgba(29, 113, 219, 0.1)'
      },

      // Text colors - mapped from UX designer dark palette
      text: {
        primary: '#E2E1EC',      // --Schemes-On-Background: rgba(226, 225, 236, 1.00)
        secondary: '#C4C5D6',    // --Schemes-On-Surface-Variant: rgba(196, 197, 214, 1.00)
        muted: '#bec0d1',        // --Schemes-Outline: rgba(142, 144, 159, 1.00)
        disabled: '#a9a9b0',     // --Palettes-Neutral-40: rgba(94, 94, 98, 1.00)
        disabledCheckbox: '#8e8e95', // Disabled text color
        label: '#E2E1EC',        // --Schemes-On-Surface: rgba(226, 225, 236, 1.00)
        caption: '#8E909F',      // --Schemes-Outline
        avatar: '#E2E1EC',       // --Schemes-On-Surface
        data: '#E2E1EC',         // --Schemes-On-Background
        hint: '#abadba',         // --Schemes-Outline
        toast: '#2F3038',        // --Schemes-Inverse-Surface: rgba(47, 48, 56, 1.00)
        highlight: '#181C36',    // --Schemes-Surface-Container
      },

      // Border colors - mapped from UX designer dark palette
      border: {
        default: '#8E909F',      // --Schemes-Outline: rgba(142, 144, 159, 1.00)
        light: '#444653',        // --Schemes-Surface-Variant: rgba(68, 70, 83, 1.00)
        muted: '#C4C5D6',        // --Schemes-Outline-Variant: rgba(196, 197, 214, 1.00)
        card: '#505159',         // --Schemes-Surface-Container
        focus: '#B6C4FF',        // --Schemes-Primary
        warning: '#CAE9FF',      // --Extended-Colors-Custom-Color-1-Container
        error: '#FFDAD6',        // --Schemes-On-Error-Container
        divider: '#444653',      // --Schemes-Surface-Variant
      },

      // Interactive states - optimized for dark mode
      hover: {
        primary: 'rgba(182, 196, 255, 0.12)',     // Primary hover background
        secondary: 'rgba(184, 196, 248, 0.12)',   // Secondary hover background
        muted: 'rgba(255, 255, 255, 0.08)',       // Muted hover background
        button: 'rgba(255, 255, 255, 0.08)',      // Button hover background
        surface: '#282A31',                        // --Schemes-Surface-Container-High
      },

      // Legacy colors adapted for dark mode
      legacy: {
        blue: {
          primary: '#B6C4FF',     // --Schemes-Primary
          dark: '#2D55C9',        // --Schemes-Primary-40
          focus: '#4870E1',       // --Schemes-Primary-Container
          light: '#B6C4FF',       // --Schemes-Primary
          material: '#B6C4FF',    // --Schemes-Primary
          accent: '#B6C4FF',      // --Schemes-Primary
          brand: '#B6C4FF',       // --Schemes-Primary
          info: '#A4CAF4',        // --Schemes-Tertiary
        },
        orange: {
          primary: '#3E6379',     // --Extended-Colors-Custom-Color-1
          light: '#CAE9FF',       // --Extended-Colors-Custom-Color-1-Container
        },
        misc: {
          teal: '#73c6d6d9',     // lighter teal (from #5cadc1d5 → +lightness)
          brown: '#5a4949',      // lighter brown
          yellow: '#e6e03a',     // lighter yellow
          gray: '#9e9e9e',       // lighter gray
          lightGray: '#e0e0e0',  // lighter light gray
          surface: '#e0ded8',    // lighter surface gray
          purple: '#9575CD',      // --Schemes-Primary
        },
      },

      // Shadows and effects - adjusted for dark backgrounds
      shadow: {
        default: '0px 0px 10px 0px rgba(0,0,0,0.4)',
        avatar: '0px 2px 6px 2px rgba(0, 0, 0, 0.3), 0px 1px 2px 0px rgba(0, 0, 0, 0.5)',
        card: '0 4px 8px rgba(0, 0, 0, 0.3)',
        elevated: '0px 8px 16px rgba(0, 0, 0, 0.4)', // Additional shadow for dark mode
      },

      // Opacity levels - same as light mode but may need adjustment
      opacity: {
        disabled: 0.38,
        hover: 0.08,
        focus: 0.12,
        selected: 0.16,
        backdrop: 0.75,
      },

      // Border colors - adjusted for dark mode
      heatmapBorder: {
        default: '#8E909F',   // --Schemes-Outline
        light: '#444653',     // --Schemes-Surface-Variant
        muted: '#C4C5D6',     // --Schemes-Outline-Variant
        card: '#1E1F27',      // --Schemes-Surface-Container
        focus: '#B6C4FF',     // --Schemes-Primary
        divider: '#444653',   // --Schemes-Surface-Variant
        critical: '#A41818',  // darker than #BA1A1A
        danger:   '#E04627',  // darker than #FB532D
        caution:  '#E6A73F',  // darker than #FFB94D
        neutral:  '#5F5E62',  // darker than #77767A
      }

    },
    mode: 'dark',
  },
};

// Create the context
const ThemeContext = createContext();

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [currentTheme, setCurrentTheme] = useState('light');
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  const theme = themes[currentTheme];

  // Fetch user theme preference on mount
  useEffect(() => {
    const fetchUserTheme = async () => {
      if (!isAuthenticated) {
        // If not authenticated, ensure we're in light mode and load
        setCurrentTheme('light');
        setIsThemeLoaded(true);
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Theme fetch timeout')), 5000)
        );

        const userDataPromise = GetUserUseQuery(token);
        const userData = await Promise.race([userDataPromise, timeoutPromise]);

        if (userData?.status === 'ok') {
          // Extract theme from the nested structure you described
          const userTheme = userData?.user?.themePreferences?.web?.theme;
          const isValidTheme = (theme) => {
            return theme === "dark" || theme === "light";
          };
          if (userTheme && isValidTheme(userTheme)) {
            setCurrentTheme(userTheme);
          } else {
            // If theme is invalid or not found, fallback to light mode
            setCurrentTheme('light');
          }
        } else {
          // If API response is not ok, fallback to light mode
          setCurrentTheme('light');
        }
      } catch (error) {
        // Silently handle theme fetch errors (including timeout) - fallback to light mode
        // This is not a critical error, just a graceful degradation
        if (error.message !== 'Theme fetch timeout') {
          // Only log non-timeout errors for debugging
          console.warn('Error fetching user theme preference:', error.message);
        }
        // On any error (including timeout), ensure we fallback to light mode
        setCurrentTheme('light');
      } finally {
        // Always set loaded to true, even on error
        setIsThemeLoaded(true);
      }
    };

    fetchUserTheme();
  }, [isAuthenticated, getAccessTokenSilently]);

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  // Apply CSS custom properties to document root for calendar styling
  React.useEffect(() => {
    const root = document.documentElement;
    const colors = theme.palette;

    // Calendar-specific CSS variables
    root.style.setProperty('--calendar-bg', colors.background.paper);
    root.style.setProperty('--calendar-primary-bg', colors.background.primary);
    root.style.setProperty('--calendar-toolbar-bg', colors.background.paper);
    root.style.setProperty('--calendar-time-header-bg', colors.background.paper);
    root.style.setProperty('--calendar-day-bg', colors.background.paper);
    root.style.setProperty('--calendar-month-bg', colors.background.primary);
    root.style.setProperty('--calendar-today-bg', colors.background.paper);
    root.style.setProperty('--calendar-label-color', colors.text.label);
    root.style.setProperty('--calendar-header-color', colors.text.label);
    root.style.setProperty('--calendar-button-color', colors.text.muted);
    root.style.setProperty('--calendar-border-color', colors.border.card);
    root.style.setProperty('--calendar-divider-color', colors.border.card);
    root.style.setProperty('--calendar-current-time', colors.error.main);
    root.style.setProperty('--calendar-today-button-bg', colors.primary.main);
    root.style.setProperty('--calendar-today-button-color', colors.primary.contrastText);
    root.style.setProperty('--calendar-now-bg', colors.primary.main);
    root.style.setProperty('--calendar-now-color', colors.primary.contrastText);
  }, [theme]);

  const value = {
    theme,
    currentTheme,
    toggleTheme,
    setTheme,
    colors: theme.palette, // Direct access to colors for convenience
    isThemeLoaded, // Expose loading state if needed
  };

  return (
    <ThemeContext.Provider value={value}>
      {isThemeLoaded ? children : (
        <Loading />
      )}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Utility functions for common color operations
export const getColor = (colorPath, theme) => {
  const colors = theme?.palette || colorPalette;
  const keys = colorPath.split('.');
  return keys.reduce((obj, key) => obj?.[key], colors);
};

export const rgba = (color, opacity) => {
  if (color.startsWith('rgba')) return color;
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

// Export the color palette for direct use if needed
export { colorPalette };

export default ThemeContext;
